import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { formatPatientDocument } from "utils/patient-documents.util";

import { emptySplitApi } from "./empty-split-api";
import {
  PatientDocument,
  PatientDocumentFilter,
  PatientDocumentInput,
  PatientDocumentResponse,
  PatientDocumentSignatureInput
} from "./types/patient-documents.type";
import {
  EDocumentBoxItem,
  EDocumentsConnectResponse,
  EDocumentsListInput,
  EDocumentsMoveInput,
  EDocumentsMoveResponse,
  EDocumentsSourceType,
  NewEDocumentsResponse
} from "./types/patient-e-documents.type";
import { BoxAccessTokenResponse } from "./types/practices.type";

export const DEFAULT_PATIENT_DOCUMENTS_QUERY =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,is_header_included}";

export const DEFAULT_PATIENT_DOCUMENTS_QUERY_WITH_PATIENT_HEADER =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,is_header_included,is_patient_header_included}";

export const DEFAULT_PATIENT_DOCUMENT_QUERY =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,order{id},sale{id},is_header_included}";

export const DEFAULT_PATIENT_DOCUMENT_QUERY_WITH_PATIENT_HEADER =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,order{id},sale{id},is_header_included,is_patient_header_included}";

/**
 * A scan (the only upload that sends `source`) is new for everyone, the person who scanned it
 * included, so their own notice refreshes too. Forms Suno saves send no `source` and never count,
 * so they leave the notice alone.
 */
export function uploadDocumentInvalidatesTags({
  id,
  type,
  formData
}: {
  id: number;
  type: EDocumentsSourceType;
  formData: FormData;
}) {
  return type === "patients" && formData.get("source")
    ? ["EDocuments" as const, { type: "NewEDocuments" as const, id }]
    : ["EDocuments" as const];
}

/** Box Upload (+) files are new for the uploader too, once Suno has recorded any of them. */
export function recordUploadsInvalidatesTags(
  result: { recorded: number } | undefined,
  patient: number
) {
  return result?.recorded
    ? [{ type: "NewEDocuments" as const, id: patient }]
    : [];
}

const patientDocumentsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientDocument: builder.query<
      PatientDocument,
      { patient: number; id: number; query?: string }
    >({
      query: ({
        patient,
        id,
        query
      }: {
        patient: number;
        id: number;
        query: string;
      }) => {
        return `/patients/${patient}/documents/${id}/?query=${
          query || DEFAULT_PATIENT_DOCUMENT_QUERY
        }`;
      },
      providesTags: ["PatientDocuments"],
      transformResponse: (response: PatientDocument) =>
        formatPatientDocument(response)
    }),
    getPatientDocuments: builder.query<
      PatientDocumentResponse,
      PatientDocumentFilter
    >({
      query: ({ patient, query, ...rest }) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          query: query || DEFAULT_PATIENT_DOCUMENTS_QUERY,
          ordering: "-created_at",
          ...rest
        });

        return `/patients/${patient}/documents/?${filtersString}`;
      },
      providesTags: ["PatientDocuments"],
      transformResponse: (response: PatientDocumentResponse) =>
        response.map(formatPatientDocument)
    }),
    createPatientDocument: builder.mutation<
      PatientDocument,
      PatientDocumentInput
    >({
      query: (input) => ({
        url: `/patients/${input.patient}/documents/`,
        method: "POST",
        body: input
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    updatePatientDocument: builder.mutation<
      PatientDocument,
      PatientDocumentInput
    >({
      query: ({ id, patient, ...rest }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "PATCH",
        body: rest
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    updatePatientDocumentSignature: builder.mutation<
      PatientDocument,
      PatientDocumentSignatureInput
    >({
      query: ({ id, patient, formData }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "PATCH",
        body: formData
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    deletePatientDocument: builder.mutation<
      PatientDocument,
      { patient: number; id: number }
    >({
      query: ({ patient, id }: { patient: number; id: number }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    uploadDocument: builder.mutation<
      PatientDocument,
      {
        id: number;
        type: EDocumentsSourceType;
        formData: FormData;
      }
    >({
      query: ({ id, type, formData }) => {
        return {
          url: `/${type}/${id}/e-documents/upload/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: (_result, _error, arg) =>
        uploadDocumentInvalidatesTags(arg)
    }),
    getBoxAccessToken: builder.query<BoxAccessTokenResponse, void>({
      query: () => "/practices/my/box-access-token/",
      providesTags: ["EDocuments"]
    }),
    getEDocumentItems: builder.query<EDocumentBoxItem[], EDocumentsListInput>({
      query: ({ type, id, folderId, offset = 0, limit = 1000 }) =>
        `/${type}/${id}/e-documents/${folderId}/?offset=${offset}&limit=${limit}`,
      providesTags: ["EDocuments"]
    }),
    updateDocumentsLocation: builder.mutation<
      EDocumentsMoveResponse,
      EDocumentsMoveInput
    >({
      query: ({ id, type, ...body }) => ({
        url: `/${type}/${id}/e-documents/move/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["EDocuments"]
    }),
    connectToEDocuments: builder.mutation<EDocumentsConnectResponse, number>({
      query: (patient: number) => ({
        url: `/patients/${patient}/e-documents/connect/`,
        method: "POST"
      }),
      invalidatesTags: ["EDocuments", "PatientDocuments"]
    }),
    // The new document notice: what this staff member has not reviewed yet on one chart.
    getNewEDocuments: builder.query<NewEDocumentsResponse, number>({
      query: (patient) => `/patients/${patient}/e-documents/new-documents/`,
      providesTags: (_result, _error, patient) => [
        { type: "NewEDocuments", id: patient }
      ]
    }),
    // Clears the notice for the signed-in person only, up to the newest document they were shown.
    // The answer is what is still new for them, written straight into the cache: a refetch could
    // land on a lagging read replica and bring the cleared notice back.
    markEDocumentsReviewed: builder.mutation<
      NewEDocumentsResponse,
      { patient: number; seenUntil?: string }
    >({
      query: ({ patient, seenUntil }) => ({
        url: `/patients/${patient}/e-documents/mark-reviewed/`,
        method: "POST",
        body: seenUntil ? { seen_until: seenUntil } : {}
      }),
      async onQueryStarted({ patient }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            patientDocumentsApi.util.upsertQueryData(
              "getNewEDocuments",
              patient,
              data
            )
          );
        } catch {
          // The hook reports the failure.
        }
      }
    }),
    // Files added with the Upload (+) button in Box's file browser go straight to Box, so the
    // page tells Suno about them afterwards. They are new for the uploader too, so the notice
    // refreshes once Suno has them.
    recordEDocumentUploads: builder.mutation<
      { recorded: number },
      { patient: number; boxFileIds: string[] }
    >({
      query: ({ patient, boxFileIds }) => ({
        url: `/patients/${patient}/e-documents/record-uploads/`,
        method: "POST",
        body: { box_file_ids: boxFileIds }
      }),
      invalidatesTags: (result, _error, { patient }) =>
        recordUploadsInvalidatesTags(result, patient)
    })
  })
});

export const {
  useGetPatientDocumentQuery,
  useGetPatientDocumentsQuery,
  useCreatePatientDocumentMutation,
  useUpdatePatientDocumentMutation,
  useUpdatePatientDocumentSignatureMutation,
  useDeletePatientDocumentMutation,
  useUploadDocumentMutation,
  useUpdateDocumentsLocationMutation,
  useGetBoxAccessTokenQuery,
  useGetEDocumentItemsQuery,
  useLazyGetBoxAccessTokenQuery,
  useConnectToEDocumentsMutation,
  useGetNewEDocumentsQuery,
  useMarkEDocumentsReviewedMutation,
  useRecordEDocumentUploadsMutation
} = patientDocumentsApi;
